import { CHERRY_WEEKS } from './definitions';
import { getWeekNumber } from './functions';
import { DEFAULT_SEASON_WEEKS, seasonHasWeek53 } from './seasonWeekOrder';

export { DEFAULT_SEASON_WEEKS };

export const SPECIES_SEASON_WEEKS: Record<number, number[]> = {
  2: CHERRY_WEEKS.weeks,
};

/**
 * Semanas de la temporada para la especie. Con `season` ("2026-2027") la 53
 * va después de la 52 solo si el año de inicio la tiene; sin `season` se
 * devuelve la lista de la especie tal cual.
 */
export const getSeasonWeeksForSpecies = (
  speciesId?: number | string | null,
  season?: string | null
): number[] => {
  const numericSpeciesId =
    typeof speciesId === 'string' ? Number(speciesId) : speciesId;

  const weeks =
    numericSpeciesId && SPECIES_SEASON_WEEKS[numericSpeciesId]
      ? SPECIES_SEASON_WEEKS[numericSpeciesId]
      : DEFAULT_SEASON_WEEKS;

  if (!season) return weeks;
  if (!seasonHasWeek53(season)) return weeks.filter((w) => w !== 53);
  const after52 = weeks.indexOf(52) + 1;
  if (!after52 || weeks.includes(53)) return weeks;
  return [...weeks.slice(0, after52), 53, ...weeks.slice(after52)];
};

export const getSeasonCutoffIndex = ({
  weeks,
  selectedSeason,
  now = new Date(),
}: {
  weeks: number[];
  selectedSeason?: string | null;
  now?: Date;
}): number => {
  const currentWeekNumber = getWeekNumber(now.toISOString());

  if (!selectedSeason) {
    if (currentWeekNumber == null) return weeks.length - 1;
    const idx = weeks.findIndex((w) => w === currentWeekNumber);
    if (idx >= 0) return idx;
    return getCutoffForWeekOutsideConfiguredWeeks(weeks, currentWeekNumber);
  }

  const seasonParts = selectedSeason.split('-');
  const startYear = parseInt(seasonParts[0], 10);
  const startDate = new Date(Date.UTC(startYear, 6, 1, 0, 0, 0));
  const endDate = new Date(Date.UTC(startYear + 1, 5, 30, 23, 59, 59));

  if (now < startDate) return -1;
  if (now > endDate) return weeks.length - 1;
  if (currentWeekNumber == null) return -1;

  const idx = weeks.findIndex((w) => w === currentWeekNumber);
  if (idx >= 0) return idx;

  return getCutoffForWeekOutsideConfiguredWeeks(weeks, currentWeekNumber);
};

const getCutoffForWeekOutsideConfiguredWeeks = (
  weeks: number[],
  currentWeekNumber: number
): number => {
  const seasonPositions = new Map(
    DEFAULT_SEASON_WEEKS.map((week, idx) => [week, idx])
  );
  const currentPosition = seasonPositions.get(currentWeekNumber);
  const configuredPositions = weeks
    .map((week, idx) => ({
      idx,
      position: seasonPositions.get(week),
    }))
    .filter(
      (weekPosition): weekPosition is { idx: number; position: number } =>
        weekPosition.position != null
    );

  if (currentPosition == null || configuredPositions.length === 0) {
    return -1;
  }

  const firstPosition = Math.min(...configuredPositions.map((w) => w.position));
  const lastPosition = Math.max(...configuredPositions.map((w) => w.position));

  if (currentPosition < firstPosition) return -1;
  if (currentPosition > lastPosition) return weeks.length - 1;

  const closestPriorWeek = configuredPositions
    .filter((w) => w.position <= currentPosition)
    .sort((a, b) => b.position - a.position)[0];

  return closestPriorWeek?.idx ?? -1;
};
